import axios from 'axios'
import React, { useState, useEffect, useRef, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Button, CalendarCell, CalendarGrid, Heading, RangeCalendar } from 'react-aria-components'
import { CalendarDate } from '@internationalized/date'
import BASE_URL from '../../../config/config'

// --- CustomSelect (igual) ---
const CustomSelect = ({ value, options, onChange }) => {
  const [open, setOpen] = useState(false)
  const currentOption = options.find(option => option.value === value)
  const selectRef = useRef(null)

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) setOpen(false)
    }
    if (open) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  return (
    <div className="custom-select-container" ref={selectRef}>
      <div className="custom-select-trigger" onClick={() => setOpen(!open)}>
        {currentOption ? currentOption.label : 'Seleccionar'}
      </div>
      {open && (
        <div className="custom-select-dropdown">
          {options.map((option) => (
            <div
              key={option.value}
              onClick={() => { onChange(option.value); setOpen(false) }}
              className={`custom-select-option ${option.value} ${option.value === value ? 'active' : ''}`}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// Toggle para Facturado (0/1) con estado de guardado
const FacturadoToggle = ({ value, onToggle, disabled }) => {
  const isOn = Number(value) === 1
  return (
    <button
      type="button"
      className={`toggle-facturado ${isOn ? 'on' : 'off'}`}
      onClick={() => onToggle(isOn ? 0 : 1)}
      title="Cambiar facturado"
      disabled={disabled}
      aria-busy={disabled}
    >
      {disabled ? 'Guardando…' : (isOn ? 'Sí' : 'No')}
    </button>
  )
}

const Proyectos = () => {
  const [proyectos, setProyectos] = useState([])

  // Filtros
  const [sortOrder, setSortOrder] = useState('newest')
  const [tipoFilter, setTipoFilter] = useState('Todos')   // Todos | desarrollo | diseno | marketing
  const [archFilter, setArchFilter] = useState('no')      // no | si | todos

  // Usuarios para asignación
  const [users, setUsers] = useState([])

  // Modal de asignación de usuario
  const [userModalOpen, setUserModalOpen] = useState(false)
  const [userModalProject, setUserModalProject] = useState(null)
  const [userQuery, setUserQuery] = useState('')
  const [userPage, setUserPage] = useState(1) // cada página = 10 usuarios
  const userModalRef = useRef(null)

  // <-- NUEVO: estado para deshabilitar el toggle por proyecto mientras guarda
  const [savingFacturado, setSavingFacturado] = useState({}) // { [idProyecto]: true|false }

  const estados = [
    { value: 'sin_ejecutar', label: 'Sin ejecutar' },
    { value: 'en_proceso', label: 'En proceso' },
    { value: 'aceptado', label: 'Aceptado' }
  ]

  const fetchProyectos = () => {
    axios.get(`${BASE_URL}/proyectos`)
      .then(response => {
        const data = response.data.proyectos ? response.data.proyectos : response.data
        setProyectos(Array.isArray(data) ? data : [])
      })
      .catch(error => console.error(error))
  }

  useEffect(() => { fetchProyectos() }, [])

  // Cargar usuarios una vez y ordenarlos (más recientes primero)
  useEffect(() => {
    axios.get(`${BASE_URL}/todos-usuarios`)
      .then(res => {
        const list = Array.isArray(res.data.usuarios) ? res.data.usuarios : []
        const getTs = (u) => {
          const candidates = [u.createdAt, u.fecha_alta, u.fechaAlta, u.created_at, u.updatedAt, u.fecha_actualizacion]
          for (const c of candidates) {
            const t = c ? new Date(c).getTime() : NaN
            if (!Number.isNaN(t)) return t
          }
          const n = Number(u.id)
          return Number.isFinite(n) ? n : 0
        }
        const sorted = [...list].sort((a, b) => getTs(b) - getTs(a))
        setUsers(sorted)
      })
      .catch(err => console.error(err))
  }, [])

  // Cerrar modal al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userModalOpen && userModalRef.current && !userModalRef.current.contains(e.target)) {
        closeUserModal()
      }
    }
    if (userModalOpen) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [userModalOpen])

  // --- Update estados (incluye usuario_autorizado y facturado) ---
  const updateEstado = async (project, field, newValue) => {
    // Guardado optimista en UI
    setProyectos(prev => prev.map(p => p.id === project.id ? { ...p, [field]: newValue } : p))

    const payload = {
      // Si justo editamos ese campo, enviamos el nuevo valor; si no, el actual del proyecto:
      prototipo: field === 'prototipo' ? newValue : project.prototipo,
      diseno: field === 'diseno' ? newValue : project.diseno,
      desarrollo: field === 'desarrollo' ? newValue : project.desarrollo,
      contenido: field === 'contenido' ? newValue : project.contenido,
      comprobacion_final: field === 'comprobacion_final' ? newValue : project.comprobacion_final,
      usuario_autorizado: field === 'usuario_autorizado' ? newValue : project.usuario_autorizado,
      facturado: field === 'facturado' ? newValue : (typeof project.facturado === 'undefined' ? 0 : project.facturado)
    }

    try {
      const response = await axios.put(`${BASE_URL}/actualizar-estados/${project.id}`, payload)
      setProyectos(prev => prev.map(p => p.id === project.id ? response.data.proyecto : p))
    } catch (error) {
      console.error('Error al actualizar estado:', error)
      // Si falla, refrescamos la lista para no dejar el optimista mal
      fetchProyectos()
    }
  }

  // Select para Facturado (0/1)
  const FacturadoSelect = ({ value, onChange, disabled }) => {
    const val = String(Number(value ?? 0)) // fuerza "0" | "1"
    return (
      <select
        className="select-facturado"
        value={val}
        onChange={(e) => onChange(Number(e.target.value))}
        disabled={disabled}
      >
        <option value="0">No</option>
        <option value="1">Sí</option>
      </select>
    )
  }


  const parseDate = (dateStr) => {
    if (!dateStr || dateStr === 'por_definir') return null
    const [day, month, year] = dateStr.split('/')
    const d = new Date(Number(year), Number(month) - 1, Number(day))
    return isNaN(d.getTime()) ? null : d
  }

  const visibleProyectos = useMemo(() => {
    const byTipo = (p) => {
      if (!tipoFilter || tipoFilter === 'Todos') return true
      return (p.tipo_de_proyecto || '').toLowerCase() === tipoFilter.toLowerCase()
    }
    const byArchivado = (p) => {
      if (archFilter === 'todos') return true
      if (archFilter === 'si') return Number(p.archivado) === 1
      return Number(p.archivado) === 0 || p.archivado === false || p.archivado === null || typeof p.archivado === 'undefined'
    }
    const filtered = (proyectos || []).filter(p => byTipo(p) && byArchivado(p))
    const sorted = [...filtered].sort((a, b) => {
      const da = parseDate(a.inicio_proyecto)
      const db = parseDate(b.inicio_proyecto)
      const ta = da ? da.getTime() : -Infinity
      const tb = db ? db.getTime() : -Infinity
      return sortOrder === 'newest' ? tb - ta : ta - tb
    })
    return sorted
  }, [proyectos, tipoFilter, archFilter, sortOrder])

  // ---------- Modal de selección de usuario ----------
  const openUserModal = (project) => {
    setUserModalProject(project)
    setUserQuery('')
    setUserPage(1) // reset página
    setUserModalOpen(true)
  }
  const closeUserModal = () => {
    setUserModalOpen(false)
    setUserModalProject(null)
    setUserQuery('')
    setUserPage(1)
  }

  useEffect(() => { setUserPage(1) }, [userQuery])

  const fullFilteredUsers = useMemo(() => {
    const q = userQuery.trim().toLowerCase()
    if (!q) return users
    return users.filter(u =>
      (u.usuario && u.usuario.toLowerCase().includes(q)) ||
      (u.nombre && u.nombre.toLowerCase().includes(q)) ||
      (u.email && u.email.toLowerCase().includes(q))
    )
  }, [users, userQuery])

  const visibleUsers = useMemo(() => fullFilteredUsers.slice(0, userPage * 10), [fullFilteredUsers, userPage])
  const hasMoreUsers = visibleUsers.length < fullFilteredUsers.length

  const assignUser = async (username) => {
    if (!userModalProject) return
    await updateEstado(userModalProject, 'usuario_autorizado', username || '')
    closeUserModal()
  }

  // Flags de vista por tipo
  const isDisenoView = (tipoFilter || '').toLowerCase() === 'diseno'
  const isMarketingView = (tipoFilter || '').toLowerCase() === 'marketing'

  // Clases dinámicas por tipo
  const tipoKey = (tipoFilter || 'Todos').toLowerCase()
  const headerClass = `tabla-header tabla-header-${tipoKey}`
  const rowClass = `tabla-row tabla-row-${tipoKey}`

  return (
    <div>
      <main className="site-width">
        <div className="grid-tracking-proyectos">
          <h2>Tracking de Proyectos</h2>
          <p>Los proyectos se sincronizan cada 10 minutos con Holded.</p>

          {/* --- FILTROS --- */}
          <div className="filtro-proyectos">
            <div className="filtro-orden">
              <select value={sortOrder} onChange={e => setSortOrder(e.target.value)}>
                <option value="newest">Más Nuevo</option>
                <option value="oldest">Más Antiguo</option>
              </select>
            </div>
            <div className="filtro-tipo">
              <select value={tipoFilter} onChange={e => setTipoFilter(e.target.value)}>
                <option value="Todos">Todos</option>
                <option value="desarrollo">Desarrollo</option>
                <option value="diseno">Diseño</option>
                <option value="marketing">Marketing</option>
              </select>
            </div>
            <div className="filtro-archivado">
              <select value={archFilter} onChange={e => setArchFilter(e.target.value)}>
                <option value="no">No archivados</option>
                <option value="si">Archivados</option>
                <option value="todos">Todos</option>
              </select>
            </div>
          </div>

          {/* --- TABLA --- */}
          <div className="tabla-proyectos">
            <div className={headerClass}>
              <div>Nombre</div>
              <div>Cliente autorizado</div>

              {/* Cabeceras condicionales */}
              {!isDisenoView && !isMarketingView && (
                <>
                  <div>Prototipo</div>
                  <div>Diseño</div>
                  <div>Desarrollo</div>
                  <div>Contenido</div>
                  <div>Validación web</div>
                </>
              )}

              {isDisenoView && (
                <>
                  <div>Diseño</div>
                  <div>Facturado</div>
                </>
              )}

              {isMarketingView && (
                <>
                  <div>Desarrollo</div>
                  <div>Facturado</div>
                </>
              )}

              <div>Inicio</div>
              <div>Fin</div>
            </div>

            {visibleProyectos.map(proyecto => (
              <div key={proyecto.id} className={rowClass}>
                <div className='nombre-proyecto'>
                  <Link to={`/dashboard/proyecto/${proyecto.id}`}>
                    {proyecto.nombre}
                  </Link>
                </div>

                {/* Usuario autorizado + modal */}
                <div className="usuario-autorizado-cell">
                  <div className="user-chip">
                    {proyecto.usuario_autorizado ? `@${proyecto.usuario_autorizado}` : '—'}
                  </div>
                  <button type="button" className="btn-assign-user" onClick={() => openUserModal(proyecto)}>
                    {proyecto.usuario_autorizado ? 'Editar' : 'Asignar'}
                  </button>
                </div>

                {/* Columnas condicionales */}
                {!isDisenoView && !isMarketingView && (
                  <>
                    <div className={`estado-cell ${proyecto.prototipo}`}>
                      <CustomSelect
                        value={proyecto.prototipo}
                        options={estados}
                        onChange={(v) => updateEstado(proyecto, 'prototipo', v)}
                      />
                    </div>
                    <div className={`estado-cell ${proyecto.diseno}`}>
                      <CustomSelect
                        value={proyecto.diseno}
                        options={estados}
                        onChange={(v) => updateEstado(proyecto, 'diseno', v)}
                      />
                    </div>
                    <div className={`estado-cell ${proyecto.desarrollo}`}>
                      <CustomSelect
                        value={proyecto.desarrollo}
                        options={estados}
                        onChange={(v) => updateEstado(proyecto, 'desarrollo', v)}
                      />
                    </div>
                    <div className={`estado-cell ${proyecto.contenido}`}>
                      <CustomSelect
                        value={proyecto.contenido}
                        options={estados}
                        onChange={(v) => updateEstado(proyecto, 'contenido', v)}
                      />
                    </div>
                    <div className={`estado-cell ${proyecto.comprobacion_final}`}>
                      <CustomSelect
                        value={proyecto.comprobacion_final}
                        options={estados}
                        onChange={(v) => updateEstado(proyecto, 'comprobacion_final', v)}
                      />
                    </div>
                  </>
                )}

                {isDisenoView && (
                  <>
                    <div className={`estado-cell ${proyecto.diseno}`}>
                      <CustomSelect
                        value={proyecto.diseno}
                        options={estados}
                        onChange={(v) => updateEstado(proyecto, 'diseno', v)}
                      />
                    </div>
                    <div className="estado-cell facturado">
                      <FacturadoSelect
                        value={proyecto.facturado ?? 0}
                        disabled={!!savingFacturado[proyecto.id]}
                        onChange={async (val) => {
                          setSavingFacturado(s => ({ ...s, [proyecto.id]: true }))
                          await updateEstado(proyecto, 'facturado', val) // guarda 0/1
                          setSavingFacturado(s => ({ ...s, [proyecto.id]: false }))
                        }}
                      />
                    </div>

                  </>
                )}

                {isMarketingView && (
                  <>
                    <div className={`estado-cell ${proyecto.desarrollo}`}>
                      <CustomSelect
                        value={proyecto.desarrollo}
                        options={estados}
                        onChange={(v) => updateEstado(proyecto, 'desarrollo', v)}
                      />
                    </div>
                    <div className="estado-cell facturado">
                      <FacturadoSelect
                        value={proyecto.facturado ?? 0}
                        disabled={!!savingFacturado[proyecto.id]}
                        onChange={async (val) => {
                          setSavingFacturado(s => ({ ...s, [proyecto.id]: true }))
                          await updateEstado(proyecto, 'facturado', val) // guarda 0/1
                          setSavingFacturado(s => ({ ...s, [proyecto.id]: false }))
                        }}
                      />
                    </div>
                  </>
                )}

                <div className='estado-cell'>{proyecto.inicio_proyecto}</div>
                <div className='estado-cell'>{proyecto.fin_proyecto}</div>
              </div>
            ))}
          </div>
        </div>
      </main>

      {/* --------- MODAL ASIGNAR USUARIO --------- */}
      {userModalOpen && (
        <div className="fg-overlay">
          <div className="modal-container user-modal" ref={userModalRef}>
            <h3>Asignar cliente autorizado</h3>
            <p>El usuario asignado tendrá acceso a ver el estado de su proyecto.</p>
            <p className="user-modal-sub">
              Proyecto: <strong>{userModalProject?.nombre}</strong>
            </p>

            <div className="user-search">
              <input
                type="text"
                placeholder="Busca por @usuario, nombre o email"
                value={userQuery}
                onChange={(e) => setUserQuery(e.target.value)}
                autoFocus
              />
            </div>

            <div className="user-results">
              {visibleUsers.length === 0 && (
                <div className="empty">No hay resultados</div>
              )}

              {visibleUsers.map(u => (
                <button
                  key={u.id}
                  className="user-result-item"
                  onClick={() => assignUser(u.usuario)}
                >
                  <span className="user-handle">@{u.usuario}</span>
                  {u.email && <span className="user-email"> — {u.email}</span>}
                </button>
              ))}
            </div>

            {hasMoreUsers && (
              <div className="user-load-more">
                <button
                  type="button"
                  className="btn-load-more"
                  onClick={() => setUserPage(p => p + 1)}
                >
                  Cargar más
                </button>
              </div>
            )}

            <div className="user-modal-actions">
              <button className="btn-close" onClick={closeUserModal}>Cancelar</button>
              <button className="btn-accept" onClick={() => assignUser('')}>Quitar asignación</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Proyectos
